import { API_BASE, ApiError, apiClient, publicApiClient, toApiError } from './api'
import { getServiceStrings } from './i18n'

export type AllowedRole = 'dev' | 'pm' | 'brse'

export interface RegisterPayload {
  email: string
  password: string
  confirmPassword: string
  name: string
  role: AllowedRole
}

export interface AuthUser {
  id: string
  email: string
  name: string
  role: string
  authProvider?: string
}

export interface AuthResponse {
  accessToken: string
  refreshToken?: string
  user: AuthUser
}

export { ApiError }

export interface LoginPayload {
  email: string
  password: string
}

/**
 * Signs in with an email and password.
 *
 * `/api`, like every other call here: the backend mounts everything under
 * `setGlobalPrefix('api')`, so a path without it reaches Express and matches no
 * route at all — which is what `registerUser` and `loginWithGoogleSso` were
 * doing until this change.
 *
 * @throws {ApiError} 401 for a wrong email or password; the server's message is
 *   Japanese, so callers show their own copy for that status.
 */
export async function loginUser(payload: LoginPayload): Promise<AuthResponse> {
  try {
    const { data } = await publicApiClient.post<AuthResponse>('/api/auth/login', payload)
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLogin)
  }
}

export async function registerUser(payload: RegisterPayload): Promise<AuthResponse> {
  try {
    const { data } = await publicApiClient.post<AuthResponse>('/api/auth/register', payload)
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedRegister)
  }
}

export async function loginWithTeamsSso(token: string): Promise<AuthResponse> {
  try {
    const { data } = await publicApiClient.post<AuthResponse>('/api/auth/sso/teams', { token })
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedMicrosoftLogin)
  }
}

export function getMicrosoftLoginUrl(): string {
  const base = API_BASE.replace(/\/api\/?$/, '')
  return `${base}/api/auth/microsoft`
}

export async function loginWithGoogleSso(token: string): Promise<AuthResponse> {
  try {
    const { data } = await publicApiClient.post<AuthResponse>('/api/auth/sso/google', { token })
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedGoogleLogin)
  }
}

export function getGoogleLoginUrl(): string {
  const base = API_BASE.replace(/\/api\/?$/, '')
  return `${base}/api/auth/google`
}

/**
 * Mint a refresh token for the access token this browser already holds.
 *
 * Only the OAuth redirect flow needs this. That flow returns its tokens through
 * the callback URL, where a 30-day credential has no business being — it would
 * land in browser history, in referrers and in logs. The callback spends its
 * short access token on this instead.
 *
 * Never throws: a session without a refresh token still works until the access
 * token expires, which is strictly better than refusing to sign the user in.
 */
export async function mintRefreshToken(): Promise<string | null> {
  try {
    const { data } = await apiClient.post<{ refreshToken: string }>('/api/auth/refresh-token')
    return data.refreshToken
  } catch {
    return null
  }
}
